import { router, usePage } from "@inertiajs/react";
import { FiArrowLeft } from "react-icons/fi";

import SummaryCard from "@/components/Cards/Investor/ResumeCard";
import Typography from "@/components/Typography";
import { moneyCentsFormatterWithoutCurrency } from "@/utils/moneyFormat";

interface SummarySectionProps {
  totalInvestors: number;
  totalCurrentValue: number;
  totalInvestedValue: number;
  totalAppreciation: number;
  investmentManagerName: string;
}

export default function SummarySection({
  totalInvestors,
  totalCurrentValue,
  totalInvestedValue,
  totalAppreciation,
  investmentManagerName,
}: SummarySectionProps) {
  const { url } = usePage();
  const urlParams = new URLSearchParams(url.split("?")[1]);
  const fromParam = urlParams.get("from");

  const isShowMode = fromParam === "admin" || fromParam === "manager";

  const handleGoBack = () => {
    if (fromParam === "admin") {
      router.get("/avo/resources/investment_managers");
    } else if (fromParam === "manager") {
      router.get("/manager/investment-managers");
    } else {
      history.back();
    }
  };

  const appreciationPercentage =
    totalInvestedValue > 0 ? (totalAppreciation / totalInvestedValue) * 100 : 0;

  return (
    <section className="flex h-fit flex-col gap-5 pt-4">
      {/* Show header when in show mode */}
      {isShowMode && (
        <div className="from-primary50 mb-4 rounded-2xl bg-secondary-900 bg-gradient-to-r p-6 shadow-md">
          <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
            <div className="flex flex-col gap-2">
              <Typography variant="overline" color="white" className="uppercase opacity-90">
                Visualizando gestor de investimentos
              </Typography>
              {investmentManagerName ? (
                <Typography variant="h3" color="white" className="font-bold">
                  {investmentManagerName}
                </Typography>
              ) : (
                <Typography variant="h3" color="white" className="font-bold">
                  Perfil do Gestor
                </Typography>
              )}
            </div>
            <div
              onClick={handleGoBack}
              className="flex cursor-pointer items-center justify-center gap-2 rounded-xl bg-white px-6 py-3 shadow-sm transition-all hover:shadow-md active:scale-95"
            >
              <FiArrowLeft className="text-primary500" size={20} />
              <Typography variant="btnLarge" color="primary500" className="font-semibold">
                Voltar
              </Typography>
            </div>
          </div>
        </div>
      )}

      <div className="flex h-fit items-center justify-between">
        <div className="flex h-full flex-col items-start gap-1 md:flex-row md:items-center md:gap-4">
          <Typography variant="h4" color="black">
            Resumo geral
          </Typography>
          <div className="hidden h-6 w-px bg-grey200 md:block" />
          <Typography variant="h6" color="secondary500" className="uppercase md:text-base">
            {totalInvestors} {totalInvestors === 1 ? "investidor" : "investidores"}
          </Typography>
        </div>
      </div>
      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-4">
        <SummaryCard
          value={totalInvestors.toString()}
          percent=""
          position="up"
          title="investidores"
          money={false}
        />
        <SummaryCard
          value={moneyCentsFormatterWithoutCurrency(totalCurrentValue)}
          percent=""
          position="up"
          title="valor atual"
          money={true}
        />
        <SummaryCard
          value={moneyCentsFormatterWithoutCurrency(totalInvestedValue)}
          percent=""
          position="up"
          title="valor investido"
          money={true}
        />
        <SummaryCard
          value={appreciationPercentage.toFixed(2) + "%"}
          percent=""
          position={appreciationPercentage >= 0 ? "up" : "down"}
          title="rentabilidade"
          money={false}
        />
      </div>
    </section>
  );
}
